import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Navbar from "@/components/karmivo/Navbar";
import HindiWarning from "@/components/karmivo/HindiWarning";
import { api, SERVICES, WORKER_COUNT_OPTIONS } from "@/lib/api";
import { collectPayment } from "@/lib/razorpay";
import { Check, Loader2, CreditCard, Copy, Clock, Phone, AlertTriangle } from "lucide-react";

const STEPS_PAID = ["Service", "Work Details", "Your Details", "Price", "Payment", "Done"];
const STEPS_REQUEST = ["Service", "Work Details", "Your Details", "Review", "Submitted"];

export default function BookWizard() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  // Phase 11: fetched from /settings/public — tells us if the Pay flow is enabled.
  const [paymentsEnabled, setPaymentsEnabled] = useState(null); // null = loading
  const [requestsOnly, setRequestsOnly] = useState(true);
  const [form, setForm] = useState({
    category: params.get("service") || "daily_labour",
    worker_count: 1,
    scheduled_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    start_time: "09:00",
    duration_hours: 4,
    location: "",
    work_description: "",
    additional_notes: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
  });
  const [quote, setQuote] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const STEPS = requestsOnly ? STEPS_REQUEST : STEPS_PAID;
  const [cancelTiers, setCancelTiers] = useState([]);
  const [policyOk, setPolicyOk] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/settings/public`);
        setRequestsOnly(!!data.booking_requests_only);
        setPaymentsEnabled(!!data.payments_enabled);
        setCancelTiers(data.cancellation_tiers || []);
      } catch (e) {
        // Fail safe: treat as request-only if we can't fetch the mode.
        setRequestsOnly(true);
        setPaymentsEnabled(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (step === 3) refreshQuote();
    // eslint-disable-next-line
  }, [step, form.category, form.worker_count, form.duration_hours]);

  const refreshQuote = async () => {
    try {
      const { data } = await api.post(`/pricing/quote`, null, {
        params: { category: form.category, worker_count: form.worker_count, duration_hours: form.duration_hours },
      });
      setQuote(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Quote failed"); }
  };

  // In REQUEST-ONLY mode this creates the booking request and jumps directly to the Submitted step.
  // In PAID mode (legacy, dev), this creates a booking then proceeds to the Pay step.
  const submitRequestOrBooking = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/bookings`, { ...form, policy_accepted: policyOk });
      setBooking(data);
      if (data.mode === "request_only" || data.status === "pending_review") {
        setStep(4); // Submitted step for request-only
      } else {
        setStep(4); // Payment step for paid mode
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setLoading(false); }
  };

  const pay = async () => {
    setLoading(true);
    try {
      const { data: order } = await api.post(`/payments/create-order`, null, { params: { booking_id: booking.id, stage: booking.two_stage ? "advance" : "full" } });
      const rzp = await collectPayment(order, { description: `Booking ${booking.code || ""}`, prefill: { name: booking.customer_name, contact: booking.customer_phone } });
      const { data } = await api.post(`/payments/verify`, {
        booking_id: booking.id, ...rzp,
        stage: booking.two_stage ? "advance" : "full",
      });
      setPaid(true);
      setBooking((b) => ({ ...b, payment_status: booking.two_stage ? "advance_paid" : "successful", payment: data.payment, dispatch: data.dispatch, closure_code: b.closure_code }));
      setStep(5);
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Payment failed"); }
    finally { setLoading(false); }
  };

  const cancelPay = () => {
    toast.warning("Payment cancelled. Booking remains Payment Pending.");
    nav("/track");
  };

  // Still loading /settings/public
  if (paymentsEnabled === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="wizard-loading">
        <Loader2 className="animate-spin text-orange-500" size={32}/>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">
        {requestsOnly && step === 0 && (
          <div data-testid="requests-only-banner" className="mb-5 p-4 rounded-lg border border-amber-300 bg-amber-50 flex items-start gap-3">
            <AlertTriangle className="text-amber-600 mt-0.5 shrink-0" size={20}/>
            <div className="text-sm text-amber-900">
              <div className="font-bold mb-1">Online payments are not available yet.</div>
              <div>Submit your booking <span className="font-semibold">request</span> and our team will call you back to confirm pricing, scheduling and payment. Nothing is charged on this screen.</div>
            </div>
          </div>
        )}

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6 text-xs" data-testid="wizard-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold ${i <= step ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-500"}`}>{i < step ? <Check size={14}/> : i + 1}</div>
              <span className={`${i <= step ? "text-slate-900 font-semibold" : "text-slate-400"} hidden sm:block`}>{s}</span>
              {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${i < step ? "bg-orange-500" : "bg-slate-200"}`}/>}
            </div>
          ))}
        </div>

        <Card className="p-6">
          {step === 0 && (
            <div data-testid="step-service">
              <h2 className="font-display text-2xl font-bold text-slate-900">What do you need?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                {SERVICES.map((s) => (
                  <button key={s.key} data-testid={`choose-service-${s.key}`} onClick={() => upd("category", s.key)}
                    className={`text-left p-4 rounded-lg border transition ${form.category === s.key ? "border-orange-500 bg-orange-50 ring-2 ring-orange-200" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="text-sm font-semibold text-slate-900">{s.label}</div>
                    <div className="text-xs text-slate-500 mt-1">₹{s.rate_per_worker_per_day}/worker/day</div>
                  </button>
                ))}
              </div>
              <div className="mt-6 flex justify-end"><Button className="bg-orange-500 hover:bg-orange-600" onClick={() => setStep(1)} data-testid="wizard-next-1-btn">Next</Button></div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-details">
              <h2 className="font-display text-2xl font-bold text-slate-900">Work Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <Label>Number of workers</Label>
                  <Select value={String(form.worker_count)} onValueChange={(v) => upd("worker_count", v === "-1" ? -1 : parseInt(v))}>
                    <SelectTrigger data-testid="worker-count-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{WORKER_COUNT_OPTIONS.map((o) => (<SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Scheduled date</Label>
                  <Input type="date" value={form.scheduled_date} onChange={(e) => upd("scheduled_date", e.target.value)} data-testid="input-date"/>
                </div>
                <div>
                  <Label>Start time</Label>
                  <Input type="time" value={form.start_time} onChange={(e) => upd("start_time", e.target.value)} data-testid="input-start"/>
                </div>
                <div>
                  <Label>Hours needed</Label>
                  <Input type="number" min="1" max="12" step="0.5" value={form.duration_hours} onChange={(e) => upd("duration_hours", parseFloat(e.target.value))} data-testid="input-hours"/>
                </div>
                <div className="sm:col-span-2">
                  <Label>Work location</Label>
                  <Input value={form.location} onChange={(e) => upd("location", e.target.value)} placeholder="Area / landmark in Jaipur" data-testid="input-location"/>
                </div>
                <div className="sm:col-span-2">
                  <Label>Describe the work</Label>
                  <Textarea rows={3} value={form.work_description} onChange={(e) => upd("work_description", e.target.value)} data-testid="input-description"/>
                </div>
                <div className="sm:col-span-2">
                  <Label>Additional notes (optional)</Label>
                  <Textarea rows={2} value={form.additional_notes} onChange={(e) => upd("additional_notes", e.target.value)} data-testid="input-notes"/>
                </div>
              </div>
              <div className="mt-6 flex justify-between"><Button variant="outline" onClick={() => setStep(0)}>Back</Button><Button className="bg-orange-500 hover:bg-orange-600" onClick={() => setStep(2)} data-testid="wizard-next-2-btn">Next</Button></div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-contact">
              <h2 className="font-display text-2xl font-bold text-slate-900">Your Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                <div><Label>Full name</Label><Input value={form.customer_name} onChange={(e) => upd("customer_name", e.target.value)} data-testid="input-name"/></div>
                <div><Label>Phone (WhatsApp)</Label><Input value={form.customer_phone} onChange={(e) => upd("customer_phone", e.target.value)} data-testid="input-phone"/></div>
                <div className="sm:col-span-2"><Label>Email (optional)</Label><Input value={form.customer_email} onChange={(e) => upd("customer_email", e.target.value)} data-testid="input-email"/></div>
              </div>
              <div className="mt-6 flex justify-between"><Button variant="outline" onClick={() => setStep(1)}>Back</Button><Button className="bg-orange-500 hover:bg-orange-600" onClick={() => setStep(3)} data-testid="wizard-next-3-btn">Next</Button></div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-review">
              <h2 className="font-display text-2xl font-bold text-slate-900">{requestsOnly ? "Review Your Request" : "Confirm & Pay Advance"}</h2>
              <HindiWarning compact />
              {quote ? (
                <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <Row k="Service" v={quote.service_label} testid="price-service"/>
                  <Row k="Workers" v={quote.worker_count} testid="price-workers"/>
                  <Row k="Days" v={quote.days} testid="price-days"/>
                  <Row k="Base amount" v={`₹${quote.labour_total}`} testid="price-base"/>
                  <Row k={`Platform fee (${quote.platform_fee_pct}%)`} v={`₹${quote.platform_fee}`} testid="price-fee"/>
                  <Row k="Subtotal" v={`₹${quote.labour_total + quote.platform_fee - (quote.discount || 0)}`} testid="price-subtotal"/>
                  <Row k={`GST (${quote.gst_pct}%)`} v={`₹${quote.gst}`} testid="price-gst"/>
                  <div className="flex justify-between px-5 py-4 bg-slate-900 text-white">
                    <span className="font-semibold">{requestsOnly ? "Estimated total" : "Total payable"}</span><span data-testid="price-total" className="font-mono font-bold text-lg">₹{quote.total}</span>
                  </div>
                  {requestsOnly ? (
                    <div className="px-5 py-4 bg-amber-50 border-t border-amber-200 text-sm text-amber-900">
                      <div className="font-semibold mb-1">This is a Booking Request. No payment is collected now.</div>
                      <div>Our team will call you to confirm pricing, schedule and arrange payment.</div>
                    </div>
                  ) : (
                    <div className="bg-orange-50 border-t border-orange-200">
                      <div className="flex justify-between px-5 py-3 text-slate-800">
                        <span className="font-semibold text-orange-700">Pay now (advance)</span>
                        <span data-testid="price-advance" className="font-mono font-semibold text-orange-700">₹500</span>
                      </div>
                      <div className="flex justify-between px-5 py-3 text-slate-700">
                        <span>Remaining (payable before work starts)</span>
                        <span data-testid="price-balance" className="font-mono">₹{Math.max(0, quote.total - 500)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : <div className="mt-6 text-slate-500">Calculating…</div>}
              <CancellationPolicy tiers={cancelTiers} total={quote?.total} accepted={policyOk} onAccept={setPolicyOk}/>
              <div className="mt-8 flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                {requestsOnly ? (
                  <Button className="bg-orange-500 hover:bg-orange-600" onClick={submitRequestOrBooking} disabled={loading || (cancelTiers.length > 0 && !policyOk)} data-testid="wizard-submit-request-btn">
                    {loading ? <Loader2 className="animate-spin mr-2" size={16}/> : <Clock size={16} className="mr-2"/>} Submit Booking Request
                  </Button>
                ) : (
                  <Button className="bg-orange-500 hover:bg-orange-600" onClick={submitRequestOrBooking} disabled={loading || (cancelTiers.length > 0 && !policyOk)} data-testid="wizard-confirm-pay-btn">
                    {loading ? <Loader2 className="animate-spin mr-2" size={16}/> : <CreditCard size={16} className="mr-2"/>} Pay ₹500 Advance
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* REQUEST-ONLY: Submitted step */}
          {requestsOnly && step === 4 && booking && (
            <div data-testid="step-request-submitted" className="text-center py-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="text-amber-600" size={32}/>
              </div>
              <h2 className="mt-4 font-display text-2xl font-bold text-slate-900">Request Submitted</h2>
              <p className="text-slate-600 mt-1">Thank you! Our team will contact you shortly to confirm.</p>
              <Card className="mt-6 p-5 max-w-md mx-auto text-left">
                <div className="flex justify-between"><span className="text-slate-500 text-sm">Reference</span><span className="font-mono font-bold" data-testid="request-reference">{booking.code}</span></div>
                <div className="flex justify-between mt-2"><span className="text-slate-500 text-sm">Status</span><span className="font-semibold text-amber-700" data-testid="request-status">Pending Review</span></div>
                <div className="flex justify-between mt-2"><span className="text-slate-500 text-sm">Service</span><span>{booking.pricing?.service_label}</span></div>
                <div className="flex justify-between mt-2"><span className="text-slate-500 text-sm">Workers</span><span>{booking.worker_count}</span></div>
                <div className="flex justify-between mt-2"><span className="text-slate-500 text-sm">Scheduled</span><span>{booking.scheduled_date} · {booking.start_time}</span></div>
                <div className="flex justify-between mt-2"><span className="text-slate-500 text-sm">Estimated total</span><span className="font-mono">₹{booking.amount}</span></div>
                <div className="flex justify-between mt-2"><span className="text-slate-500 text-sm">Payment collected</span><span className="font-semibold text-slate-600">None (requests only)</span></div>
              </Card>
              <Card className="mt-4 p-5 max-w-md mx-auto bg-amber-50 border-amber-200 text-left">
                <div className="flex items-start gap-3">
                  <Phone className="text-amber-600 mt-1" size={18}/>
                  <div>
                    <div className="font-bold text-amber-900 text-sm">What happens next?</div>
                    <ul className="text-xs text-amber-900 mt-1 list-disc pl-4 space-y-1">
                      <li>Our team reviews your request and calls you on <span className="font-mono">{booking.customer_phone}</span>.</li>
                      <li>We confirm scheduling, pricing and workers.</li>
                      <li>Payment is arranged over the call — <span className="font-semibold">no payment is collected on this screen.</span></li>
                    </ul>
                  </div>
                </div>
              </Card>
              <div className="mt-6 flex gap-2 justify-center">
                <Button variant="outline" onClick={() => nav("/")} data-testid="done-home-btn">Home</Button>
                <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => nav(`/track?code=${booking.code}`)} data-testid="done-track-btn">Track Request</Button>
              </div>
            </div>
          )}

          {/* PAID MODE: Payment step (legacy) */}
          {!requestsOnly && step === 4 && booking && (
            <div data-testid="step-payment">
              <h2 className="font-display text-2xl font-bold text-slate-900">Secure Payment — Booking Advance</h2>
              <HindiWarning compact />
              <Card className="mt-5 p-5 bg-slate-50 border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Booking</div>
                    <div className="font-mono font-bold text-slate-900">{booking.code}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Pay Now</div>
                    <div className="text-orange-600 font-bold text-2xl font-mono">₹{booking.advance_amount || booking.amount}</div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-600 space-y-1">
                  <div className="flex justify-between"><span>Total booking amount</span><span className="font-mono">₹{booking.amount}</span></div>
                  <div className="flex justify-between"><span>Paying now (advance)</span><span className="font-mono font-semibold text-orange-600">₹{booking.advance_amount || booking.amount}</span></div>
                  <div className="flex justify-between"><span>Remaining (before work starts)</span><span className="font-mono">₹{booking.balance_amount ?? 0}</span></div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-xs text-slate-600">
                  <img src="https://cdn.razorpay.com/static/assets/logo/payment.svg" alt="Razorpay" className="h-5"/>
                  <span>Payments processed via Razorpay. Server-verified.</span>
                </div>
              </Card>
              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={cancelPay} data-testid="wizard-cancel-pay-btn">Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={pay} disabled={loading} data-testid="wizard-pay-now-btn">
                  {loading ? <Loader2 className="animate-spin mr-2" size={16}/> : <CreditCard size={16} className="mr-2"/>} Pay ₹{booking.advance_amount || booking.amount}
                </Button>
              </div>
            </div>
          )}

          {/* PAID MODE: Done step (legacy) */}
          {!requestsOnly && step === 5 && booking && paid && (
            <div data-testid="step-done" className="text-center py-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="text-emerald-600" size={32}/>
              </div>
              <h2 className="mt-4 font-display text-2xl font-bold text-slate-900">Booking Confirmed!</h2>
              <p className="text-slate-600 mt-1">Payment verified. Our AI is now dispatching workers.</p>
              <Card className="mt-6 p-5 max-w-md mx-auto text-left">
                <div className="flex justify-between"><span className="text-slate-500 text-sm">Booking ID</span><span className="font-mono font-bold">{booking.code}</span></div>
                <div className="flex justify-between mt-2"><span className="text-slate-500 text-sm">Amount Paid</span><span className="font-bold text-emerald-600">₹{booking.amount}</span></div>
                <div className="flex justify-between mt-2"><span className="text-slate-500 text-sm">Workers Requested</span><span>{booking.worker_count}</span></div>
                <div className="flex justify-between mt-2"><span className="text-slate-500 text-sm">Initially Assigned</span><span className="font-mono">{booking.dispatch?.total_assigned ?? 0}/{booking.worker_count}</span></div>
              </Card>
              <Card className="mt-4 p-5 max-w-md mx-auto border-2 border-orange-300 bg-orange-50">
                <div className="text-xs uppercase tracking-widest text-orange-700 font-bold">Your Closure Code (keep it safe)</div>
                <div className="mt-2 flex items-center justify-center gap-2">
                  <div className="font-mono text-4xl font-extrabold text-orange-700 tracking-widest" data-testid="closure-code-display">{booking.closure_code}</div>
                  <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(booking.closure_code); toast.success("Copied"); }}><Copy size={14}/></Button>
                </div>
                <p className="text-xs text-orange-800 mt-2">Share this code with the worker ONLY after work is completed.</p>
              </Card>
              <div className="mt-6 flex gap-2 justify-center">
                <Button variant="outline" onClick={() => nav("/")} data-testid="done-home-btn">Home</Button>
                <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => nav(`/track?code=${booking.code}`)} data-testid="done-track-btn">Track Booking</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between px-5 py-3" data-testid={testid}>
    <span className="text-slate-600">{k}</span><span className="font-mono font-semibold text-slate-900">{v}</span>
  </div>
);

function CancellationPolicy({ tiers, total, accepted, onAccept }) {
  if (!tiers?.length) return null;
  const sorted = [...tiers].sort((a, b) => b.min_hours_before - a.min_hours_before);
  const label = (t, i) => (i === 0 ? `${t.min_hours_before}+ hours before start` : `${t.min_hours_before}–${sorted[i - 1].min_hours_before} hours before`);
  return (
    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="cancellation-policy">
      <div className="font-semibold text-slate-900 text-sm">Cancellation charges</div>
      <ul className="mt-2 space-y-1 text-sm">
        {sorted.map((t, i) => (
          <li key={i} className="flex justify-between" data-testid={`cancellation-tier-${i}`}>
            <span className="text-slate-600">{t.min_hours_before === 0 && i > 0 ? `Less than ${sorted[i - 1].min_hours_before} hours before` : label(t, i)}</span>
            <span className="font-mono font-semibold">{t.fee_pct === 0 ? "Free" : `${t.fee_pct}%${total ? ` (₹${Math.round(total * t.fee_pct / 100)})` : ""}`}</span>
          </li>
        ))}
      </ul>
      <label className="mt-3 flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={accepted} onChange={(e) => onAccept(e.target.checked)} className="mt-1 accent-orange-500" data-testid="cancellation-policy-accept"/>
        I have read and accept the cancellation charges. They stay fixed for this booking.
      </label>
    </div>
  );
}
